/**
 * Service Add-on Selector
 * ------------------------
 * - Recalculates the displayed total price as add-ons are (de)selected.
 * - Keeps the theme's NATIVE Buy button completely untouched, visually and
 *   in the DOM — no hiding, no replacing, no second button. Instead, a
 *   single form-submit listener is attached on `document` in the CAPTURING
 *   phase. It runs before the theme's product-form handler and matches the
 *   widget to the exact form containing Shopify's native Add to cart button.
 *   That lets us reliably intercept the submission, stop it from reaching the app/theme's
 *   own handler, and run our own combined add-to-cart — while leaving the
 *   button's appearance and default behavior completely alone whenever no
 *   add-ons are selected.
 *
 * No app required — runs on Shopify's standard Ajax Cart API
 * (/cart/add.js), so it works with any theme.
 */
(function () {
  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    var value = (cents / 100).toFixed(2);
    return format && format.indexOf('{{amount}}') > -1
      ? format.replace('{{amount}}', value)
      : '$' + value;
  }

  var instances = [];

  function ServiceAddons(root) {
    this.root = root;
    this.basePrice = parseInt(root.dataset.productPrice, 10) || 0;
    this.productTitle = root.dataset.productTitle || '';
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.checkboxes = Array.prototype.slice.call(
      root.querySelectorAll('.service-addon-item__checkbox')
    );
    this.totalPriceEl = root.querySelector('[data-total-price]');
    var configuredForm =
      root.dataset.productFormId && document.getElementById(root.dataset.productFormId);
    var cartForms = Array.prototype.slice.call(
      document.querySelectorAll('form[action*="/cart/add"]')
    );
    this.form =
      configuredForm ||
      root.closest('form') ||
      cartForms.find(function (form) {
        return form.querySelector('input[name="id"]') &&
          form.querySelector('button[name="add"], [type="submit"]');
      }) ||
      null;
    this.nativeSubmit = this.form ? this.form.querySelector('[type="submit"]') : null;
    this.bindEvents();
    this.updateTotal();
    instances.push(this);
  }

  ServiceAddons.prototype.bindEvents = function () {
    var self = this;
    this.bundleCheckboxes = this.checkboxes.filter(function (cb) {
      return cb.dataset.isBundle === 'true';
    });
    this.individualCheckboxes = this.checkboxes.filter(function (cb) {
      return cb.dataset.isBundle !== 'true';
    });

    this.checkboxes.forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        self.handleExclusivity(checkbox);
        self.updateTotal();
      });
    });
  };

  // A "bundle" item (e.g. Full Service Package) is mutually exclusive with
  // the individual services it replaces.
  ServiceAddons.prototype.handleExclusivity = function (changed) {
    var isBundle = changed.dataset.isBundle === 'true';

    if (isBundle && changed.checked) {
      this.individualCheckboxes.forEach(function (cb) {
        cb.checked = false;
        cb.disabled = true;
        cb.closest('.service-addon-item').classList.add('service-addon-item--disabled');
      });
    } else if (isBundle && !changed.checked) {
      this.individualCheckboxes.forEach(function (cb) {
        cb.disabled = false;
        cb.closest('.service-addon-item').classList.remove('service-addon-item--disabled');
      });
    } else if (!isBundle) {
      var anyIndividualChecked = this.individualCheckboxes.some(function (cb) {
        return cb.checked;
      });
      this.bundleCheckboxes.forEach(function (cb) {
        cb.disabled = anyIndividualChecked;
        if (anyIndividualChecked) cb.checked = false;
        cb.closest('.service-addon-item').classList.toggle('service-addon-item--disabled', anyIndividualChecked);
      });
    }
  };

  ServiceAddons.prototype.updateTotal = function () {
    var addonsTotal = this.checkboxes
      .filter(function (cb) {
        return cb.checked;
      })
      .reduce(function (sum, cb) {
        return sum + (parseInt(cb.dataset.price, 10) || 0);
      }, 0);

    var total = this.basePrice + addonsTotal;

    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = formatMoney(total, this.moneyFormat);
    }
  };

  ServiceAddons.prototype.hasSelection = function () {
    return this.checkboxes.some(function (cb) {
      return cb.checked;
    });
  };

  ServiceAddons.prototype.addToCart = function (triggerBtn) {
    var selected = this.checkboxes.filter(function (cb) {
      return cb.checked;
    });

    if (!this.form) {
      console.error('Service Add-ons: could not find the product form.');
      return;
    }

    var formData = new FormData(this.form);
    var mainVariantId = formData.get('id');
    var quantity = parseInt(formData.get('quantity') || '1', 10);
    var bundleId = 'bundle_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);

    var items = [
      {
        id: mainVariantId,
        quantity: quantity,
        properties: { _bundle_id: bundleId }
      }
    ];

    selected.forEach(function (cb) {
      items.push({
        id: cb.dataset.variantId,
        quantity: quantity,
        properties: {
          _bundle_id: bundleId,
          _service_for: this.productTitle,
          _service_name: cb.dataset.title
        }
      });
    }, this);

    var originalLabel = triggerBtn ? triggerBtn.textContent : null;
    if (triggerBtn) {
      triggerBtn.disabled = true;
      triggerBtn.textContent = 'Adding\u2026';
    }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) {
          throw new Error(data.description || 'Could not add items to cart');
        }
        document.documentElement.dispatchEvent(
          new CustomEvent('service-addons:cart-updated', { detail: data, bubbles: true })
        );
        window.location.href = root + 'cart';
      })
      .catch(function (err) {
        console.error('Service Add-ons: add to cart failed', err);
        alert('Sorry, something went wrong adding your selected items to the cart.');
        if (triggerBtn) {
          triggerBtn.disabled = false;
          triggerBtn.textContent = originalLabel;
        }
      });
  };

  document.querySelectorAll('.service-addons').forEach(function (el) {
    new ServiceAddons(el);
  });

  // Intercept the exact Shopify product form submission. This works even
  // when a theme outputs extra /cart/add forms for payment terms or sticky
  // product UI, because each widget instance is matched to its real form.
  document.addEventListener(
    'submit',
    function (event) {
      var instance = instances.find(function (inst) {
        return inst.form === event.target;
      });
      if (!instance) return; // not one of our tracked buy buttons
      if (!instance.hasSelection()) return; // no add-ons picked, let native flow run untouched

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      instance.addToCart(instance.nativeSubmit);
    },
    true
  );
})();
